import React, { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Mail,
  User,
  Building2,
  Phone,
  Briefcase,
  CircleDollarSign,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { LEISTUNG_OPTIONS, BUDGET_OPTIONS } from "../data/mock";
import { submitLead } from "../lib/api";

const ContactSection = ({ extended = false, defaultLeistung = "" }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    leistung: defaultLeistung || (extended ? LEISTUNG_OPTIONS[0] : ""),
    budget: extended ? BUDGET_OPTIONS[0] : "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Bitte Pflichtfelder ausfüllen",
        description: "Name, E-Mail und Nachricht sind erforderlich.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        name: form.name,
        email: form.email,
        company: form.company || "",
        phone: form.phone || "",
        leistung: form.leistung || "",
        budget: form.budget || "",
        message: form.message,
      });
      setSent(true);
      toast({
        title: "Anfrage gesendet ✓",
        description:
          "Wir melden uns innerhalb von 24h bei dir. Eine Bestätigung ist unterwegs.",
      });
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        leistung: extended ? LEISTUNG_OPTIONS[0] : "",
        budget: extended ? BUDGET_OPTIONS[0] : "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Senden fehlgeschlagen. Bitte versuche es erneut oder schreibe uns direkt an info@freeayto.de.";
      toast({
        title: "Etwas ist schiefgelaufen",
        description:
          typeof detail === "string" ? detail : "Bitte versuche es erneut.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="py-24 md:py-36 bg-neutral-950 text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.18),rgba(249,115,22,0.12)_30%,rgba(168,85,247,0.08)_50%,transparent_70%)]" />
      </div>

      <div className="max-w-[1300px] mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.2em] font-semibold text-neutral-300">
              Lass uns reden
            </span>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[1.02] mt-6">
              Bereit für Content,{" "}
              <span className="bg-gradient-to-r from-pink-400 via-orange-400 to-purple-400 bg-clip-text text-transparent">
                der verkauft?
              </span>
            </h2>
            <p className="mt-6 text-lg text-neutral-300 leading-relaxed max-w-xl">
              Vereinbare ein kostenloses Erstgespräch. Wir schauen uns euren
              aktuellen Auftritt an und zeigen euch, wo das Potenzial liegt –
              ohne Verpflichtung.
            </p>

            <div className="mt-10 space-y-4 max-w-md">
              {[
                "30 Min. kostenlose Analyse",
                "Individuelle Empfehlungen",
                "Keine Verkaufs-Show",
                "Antwort innerhalb 24h",
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2
                    size={20}
                    className="text-orange-400 shrink-0"
                  />
                  <span className="text-neutral-200">{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="p-7 md:p-10 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10"
            >
              <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">
                Anfrage senden
              </h3>
              <p className="mt-1 text-sm text-neutral-400">
                Wir melden uns innerhalb von 24 Stunden.
              </p>

              <div className="mt-7 grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <User
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
                  />
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Name *"
                    className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
                  />
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="E-Mail *"
                    className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>
                <div className="relative">
                  <Building2
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
                  />
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Unternehmen"
                    className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>
                <div className="relative">
                  <Phone
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
                  />
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Telefon (optional)"
                    className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>
              </div>

              {extended && (
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <Briefcase
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 z-10"
                    />
                    <select
                      name="leistung"
                      value={form.leistung}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white transition-colors appearance-none"
                    >
                      {LEISTUNG_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-neutral-900">
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative">
                    <CircleDollarSign
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 z-10"
                    />
                    <select
                      name="budget"
                      value={form.budget}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white transition-colors appearance-none"
                    >
                      {BUDGET_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-neutral-900">
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Worum geht es? *"
                  rows={4}
                  className="w-full px-4 py-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white placeholder:text-neutral-500 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-6 w-full group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold tracking-[0.12em] uppercase hover:shadow-[0_18px_42px_-10px_rgba(236,72,153,0.7)] hover:-translate-y-[2px] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Senden…" : sent ? "Nochmal senden" : "Anfrage abschicken"}
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
              <p className="mt-4 text-[11px] text-neutral-500 text-center">
                Kostenlos & unverbindlich. Mit dem Absenden stimmst du unserer
                Datenschutzerklärung zu.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
